import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Gift, Plus, Edit2, Trash2, Calendar, Percent, Euro, Toggle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { getBackendUrl } from "@/utils/apiBase";

const API = getBackendUrl();

export const PromotionsManager = () => {
  const [promotions, setPromotions] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPromotion, setEditingPromotion] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    discount_type: "percentage",
    discount_value: "",
    start_date: "",
    end_date: "",
    description: "",
    is_active: true,
  });

  useEffect(() => {
    fetchPromotions();
  }, []);

  const fetchPromotions = async () => {
    try {
      const { data } = await axios.get(`${API}/api/promotions`);
      setPromotions(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erreur lors du chargement des promotions:", error);
      toast.error(error.response?.data?.detail || "Erreur lors du chargement des promotions");
    }
  };

  const handleOpenDialog = (promotion = null) => {
    if (promotion) {
      setEditingPromotion(promotion);
      setFormData({
        title: promotion.title,
        discount_type: promotion.discount_type,
        discount_value: promotion.discount_value,
        start_date: promotion.start_date,
        end_date: promotion.end_date,
        description: promotion.description || "",
        is_active: promotion.is_active,
      });
    } else {
      setEditingPromotion(null);
      setFormData({
        title: "",
        discount_type: "percentage",
        discount_value: "",
        start_date: "",
        end_date: "",
        description: "",
        is_active: true,
      });
    }
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData, discount_value: parseFloat(formData.discount_value) };
      if (editingPromotion) {
        await axios.put(`${API}/api/promotions/${editingPromotion.id}`, payload);
        toast.success("Promotion modifiée");
      } else {
        await axios.post(`${API}/api/promotions`, payload);
        toast.success("Promotion créée");
      }
      setIsDialogOpen(false);
      fetchPromotions();
    } catch (error) {
      console.error("Erreur:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la sauvegarde");
    }
  };

  const handleDelete = async (promotionId) => {
    if (!window.confirm("Supprimer cette promotion ?")) return;
    try {
      await axios.delete(`${API}/api/promotions/${promotionId}`);
      toast.success("Promotion supprimée");
      fetchPromotions();
    } catch (error) {
      console.error("Erreur:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la suppression");
    }
  };

  const isPromotionValid = (promotion) => {
    const today = new Date();
    const start = new Date(promotion.start_date);
    const end = new Date(promotion.end_date);
    return promotion.is_active && start <= today && today <= end;
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2 font-['Playfair_Display']">
            <Gift className="h-6 w-6 sm:h-8 sm:w-8 text-[#b8977e]" />
            Gestion des Promotions
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Créez et gérez vos promotions saisonnières
          </p>
        </div>
        <Button
          onClick={() => handleOpenDialog()}
          className="bg-[#b8977e] hover:bg-[#a68770] text-white w-full sm:w-auto rounded-lg"
        >
          <Plus className="mr-2 h-4 w-4" />
          Nouvelle Promotion
        </Button>
      </div>

      {/* Promotions List */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {promotions.map((promo) => (
          <Card key={promo.id} className={`${isPromotionValid(promo) ? 'border-[#b8977e] bg-[#fef9f5]' : 'border-[#f0e6d3]'} hover:shadow-lg transition-shadow rounded-xl`}>
            <CardHeader className="pb-3">
              <div className="flex justify-between items-start gap-2">
                <CardTitle className="text-base sm:text-lg flex items-center gap-2 flex-wrap font-['Playfair_Display']">
                  {promo.title}
                  {isPromotionValid(promo) && (
                    <Badge className="bg-[#b8977e] text-white text-xs">Active</Badge>
                  )}
                  {!promo.is_active && (
                    <Badge variant="secondary" className="text-xs">Désactivée</Badge>
                  )}
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-2 text-2xl sm:text-3xl font-bold text-[#b8977e]">
                {promo.discount_type === "percentage" ? (
                  <>
                    <Percent className="h-5 w-5 sm:h-6 sm:w-6" />
                    -{promo.discount_value}%
                  </>
                ) : (
                  <>
                    <Euro className="h-5 w-5 sm:h-6 sm:w-6" />
                    -{promo.discount_value}€
                  </>
                )}
              </div>

              {promo.description && (
                <p className="text-xs sm:text-sm text-gray-600">{promo.description}</p>
              )}

              <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-500">
                <Calendar className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="truncate">{new Date(promo.start_date).toLocaleDateString('fr-FR')} → {new Date(promo.end_date).toLocaleDateString('fr-FR')}</span>
              </div>

              <div className="flex gap-2 pt-2">
                <Button
                  onClick={() => handleOpenDialog(promo)}
                  size="sm"
                  variant="outline"
                  className="flex-1 text-xs sm:text-sm"
                >
                  <Edit2 className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                  Éditer
                </Button>
                <Button
                  onClick={() => handleDelete(promo.id)}
                  size="sm"
                  variant="outline"
                  className="flex-1 text-red-600 hover:bg-red-50 text-xs sm:text-sm"
                >
                  <Trash2 className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                  Supprimer
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}

        {promotions.length === 0 && (
          <Card className="col-span-full">
            <CardContent className="py-12 text-center">
              <Gift className="h-12 w-12 sm:h-16 sm:w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-sm sm:text-base text-gray-500">Aucune promotion créée</p>
              <Button
                onClick={() => handleOpenDialog()}
                className="mt-4 bg-[#b8977e] hover:bg-[#a68770] text-white rounded-lg"
              >
                <Plus className="mr-2 h-4 w-4" />
                Créer la première promotion
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-md sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingPromotion ? "Modifier la Promotion" : "Nouvelle Promotion"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="title">Titre de la promotion *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="ex: Halloween -15%"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="discount_type">Type de réduction *</Label>
                <select
                  id="discount_type"
                  value={formData.discount_type}
                  onChange={(e) => setFormData({ ...formData, discount_type: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  required
                >
                  <option value="percentage">Pourcentage (%)</option>
                  <option value="fixed">Montant fixe (€)</option>
                </select>
              </div>

              <div>
                <Label htmlFor="discount_value">Valeur *</Label>
                <Input
                  id="discount_value"
                  type="number"
                  step="0.01"
                  value={formData.discount_value}
                  onChange={(e) => setFormData({ ...formData, discount_value: e.target.value })}
                  placeholder={formData.discount_type === "percentage" ? "15" : "20.00"}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="start_date">Date de début *</Label>
                <Input
                  id="start_date"
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label htmlFor="end_date">Date de fin *</Label>
                <Input
                  id="end_date"
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  required
                />
              </div>
            </div>

            <div>
              <Label htmlFor="description">Description (optionnelle)</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Promotion spéciale Halloween..."
                rows={3}
              />
            </div>

            <div className="flex items-center gap-3">
              <input
                id="is_active"
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="w-4 h-4 text-rose-600"
              />
              <Label htmlFor="is_active" className="cursor-pointer">
                Promotion active
              </Label>
            </div>

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Annuler
              </Button>
              <Button type="submit" className="bg-[#b8977e] hover:bg-[#a68770] text-white rounded-lg">
                {editingPromotion ? "Modifier" : "Créer"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
